// frontend/src/pages/Rosters/RosterEditPage.js
// Edit a roster: name, description, and membership.
// Filters behavior aligns with Award Points → Individual Students:
// - studentsApi.list(query)
// - Debounced search, house_id, normalized numeric grade
// - page/page_size, Refresh/Reset
// - Houses from /api/houses/summary (with robust fallbacks), Grades built from rows
//   and optionally pre-seeded from /api/students/grades/summary if available.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../api/client";
import { rostersApi } from "../../api/rostersApi";
import { studentsApi } from "../../api/studentsApi";

// Seed the grade dropdown with 1–12 so Grade 1 is always available
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

// Debounce helper (same as in StudentSelectPage)
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

// Grade normalizer (copied to match StudentSelectPage semantics)
function normalizeGrade(input) {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim();
  if (/^\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isNaN(n)) return null;
    if (n < 0 || n > 12) return null; // adjust if your school differs
    return n;
  }
  const digits = raw.replace(/[^0-9]/g, "");
  if (!digits) return null;
  const n = Number(digits);
  if (Number.isNaN(n)) return null;
  if (n < 0 || n > 12) return null;
  return n;
}

export default function RosterEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Selection state
  const [selected, setSelected] = useState(new Set());

  // Loading & error
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Students + filters + paging (match StudentSelectPage)
  const [rows, setRows] = useState([]);  // students for current page
  const [count, setCount] = useState(0); // total results (if provided)
  const [err, setErr] = useState("");

  const [search, setSearch] = useState("");
  const [houseId, setHouseId] = useState(""); // send ID, not name
  const [grade, setGrade] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Dropdown data
  const [houses, setHouses] = useState([]);                 // [{id,name}]
  const [allGrades, setAllGrades] = useState(GRADE_OPTIONS); // ✅ seed with 1–12

  const debouncedSearch = useDebounced(search, 300);

  const query = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (debouncedSearch) p.q = debouncedSearch;
    if (houseId) p.house_id = houseId;           // backend expects house_id
    if (grade) {
      const ng = normalizeGrade(grade);
      if (ng !== null) p.grade = ng;             // SEND NUMERIC
      else p.grade = grade;                      // fallback (won't break)
    }
    return p;
  }, [debouncedSearch, houseId, grade, page, pageSize]);

  // Helpers
  const toId = (s) => String(s?.id ?? s?.student_id ?? s?.uuid);
  const toDisplayName = (s) => {
    let displayName = s?.name;
    if (displayName == null || String(displayName).trim() === "") {
      const composed = `${s?.first_name ?? ""} ${s?.last_name ?? ""}`.trim();
      displayName = composed !== "" ? composed : `Student ${toId(s)}`;
    }
    return displayName;
  };

  // --- Robust Houses loader (summary first, then fallbacks; multiple shapes) ---
  async function loadHouses() {
    let items = [];
    try {
      const h = await api.get("/api/houses/summary", { auth: false });
      const data = h?.data;
      if (Array.isArray(data?.results)) items = data.results;
      else if (Array.isArray(data?.houses)) items = data.houses;
      else if (Array.isArray(data?.data)) items = data.data;
      else if (Array.isArray(data)) items = data;
    } catch {}
    if (!Array.isArray(items) || items.length === 0) {
      try {
        const h2 = await api.get("/api/houses", { auth: false });
        const data2 = h2?.data;
        if (Array.isArray(data2?.results)) items = data2.results;
        else if (Array.isArray(data2?.houses)) items = data2.houses;
        else if (Array.isArray(data2?.data)) items = data2.data;
        else if (Array.isArray(data2)) items = data2;
      } catch {}
    }
    const mapped = (Array.isArray(items) ? items : []).map((r) => {
      const idVal = r?.id ?? r?.house_id ?? r?.uuid;
      const nameVal = r?.name ?? r?.title ?? r?.house_name;
      const idStr = idVal != null ? String(idVal) : "";
      const label = nameVal != null && String(nameVal).trim() !== "" ? nameVal : (idStr ? `House ${idStr}` : "House");
      return { id: idStr, name: label };
    });
    setHouses(mapped);
  }

  // --- Optional Grades summary pre-seed (then accumulate from rows like StudentSelectPage) ---
  async function preloadGrades() {
    try {
      const g = await api.get("/api/students/grades/summary", { auth: false });
      const data = g?.data;
      const rawArr = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data?.grades)
          ? data.grades
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data)
              ? data
              : [];
      const set = new Set();
      rawArr.forEach((item) => {
        let val = item;
        if (item && typeof item === "object") {
          if (item.grade !== undefined && item.grade !== null) val = item.grade;
          else if (item.grade_level !== undefined && item.grade_level !== null) val = item.grade_level;
          else if (item.value !== undefined && item.value !== null) val = item.value;
          else if (item.name !== undefined && item.name !== null) val = item.name;
        }
        const ng = normalizeGrade(val);
        if (ng !== null) set.add(String(ng));
      });
      if (set.size > 0) {
        const arr = Array.from(set);
        arr.sort((a, b) => Number(a) - Number(b));
        // ✅ Merge with seeded 1–12 instead of overwriting
        setAllGrades((prev) => {
          const merged = new Set(prev);
          arr.forEach((g) => merged.add(g));
          return Array.from(merged).sort((a, b) => Number(a) - Number(b));
        });
      }
    } catch {
      // Not critical; we'll still accumulate from rows below.
    }
  }

  // Load students via studentsApi.list (same as StudentSelectPage)
  async function loadStudents() {
    setErr("");
    try {
      const res = await studentsApi.list(query);
      if (!res.ok) {
        setRows([]);
        setCount(0);
        setErr(`Failed to load students (${res.status}).`);
        return;
      }
      setRows(res.students);
      setCount(typeof res.count === "number" ? res.count : res.students.length);
    } catch (e) {
      setRows([]);
      setCount(0);
      setErr(e?.message || "Unexpected error.");
    }
  }

  // Preload roster + initial students page + houses + (optional) grade summary
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        // Load roster details from either endpoint
        let rosterRes = await api.get(`/api/rosters/${id}`);
        if (!(rosterRes?.status >= 200 && rosterRes?.status < 300)) {
          rosterRes = await api.get(`/api/groups/${id}`);
        }
        const roster = rosterRes.data;
        const rName = roster?.name ?? roster?.title ?? "";
        const rDesc = roster?.description ?? "";
        setName(rName);
        setDescription(rDesc);

        // Existing member IDs
        let memberIds = roster?.student_ids ?? roster?.member_ids;
        if (memberIds == null && Array.isArray(roster?.students)) {
          memberIds = roster.students.map((s) => String(s?.id ?? s?.student_id ?? s?.uuid));
        }
        if (memberIds == null && Array.isArray(roster?.members)) {
          memberIds = roster.members.map((s) => String(s?.id ?? s?.student_id ?? s?.uuid));
        }
        const initial = new Set((memberIds ?? []).map(String));
        setSelected(new Set(initial));

        // Preload dropdowns; don't block UI if they fail
        await Promise.allSettled([loadHouses(), preloadGrades()]);

        // First page of students
        await loadStudents();
      } catch (e) {
        setError("Unable to load roster.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Build Grades dropdown from loaded rows (accumulate as user browses) — same as StudentSelectPage
  useEffect(() => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setAllGrades((prev) => {
      const set = new Set(prev);
      rows.forEach((r) => {
        const rawVal = (r?.grade ?? r?.grade_level ?? "").toString().trim();
        const ng = normalizeGrade(rawVal);
        if (ng !== null) set.add(String(ng));
      });
      const arr = Array.from(set);
      arr.sort((a, b) => Number(a) - Number(b));
      return arr;
    });
  }, [rows]);

  // Reload whenever the query changes (match StudentSelectPage)
  useEffect(() => {
    if (loading) return; // skip first-run; already loaded
    loadStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  // Selection helpers (persist across pages)
  const isSelected = (sid) => selected.has(String(sid));
  const toggleOne = (sid) => {
    const key = String(sid);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const visibleIds = rows.map((r) => String(r?.id ?? r?.student_id ?? r?.uuid));
  const allOnPageChecked =
    visibleIds.length > 0 && visibleIds.every((id) => isSelected(id));
  const someOnPageChecked =
    !allOnPageChecked && visibleIds.some((id) => isSelected(id));

  const headerRef = useRef(null);
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someOnPageChecked;
  }, [someOnPageChecked]);

  const togglePage = () => {
    const next = new Set(selected);
    if (allOnPageChecked) {
      visibleIds.forEach((id) => next.delete(String(id)));
    } else {
      visibleIds.forEach((id) => next.add(String(id)));
    }
    setSelected(next);
  };

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    setError("");

    const studentIds = Array.from(selected);

    const res = await rostersApi.update({
      id,
      name: name?.trim(),
      description: description?.trim(),
      studentIds,
    });

    setSaving(false);

    if (!res.ok) {
      setError(res.error || "Unable to save changes.");
      return;
    }
    navigate("/rosters");
  }

  const totalPages =
    count && pageSize ? Math.max(1, Math.ceil(count / pageSize)) : null;

  const onRefresh = () => {
    setPage(1);
    loadStudents();
  };

  const onReset = () => {
    setSearch("");
    setHouseId("");
    setGrade("");
    setPage(1);
  };

  return (
    <div style={styles.page}>
      <h1 style={{ marginTop: 0 }}>Edit Roster</h1>

      {loading && <div>Loading…</div>}
      {error && <div style={styles.error}>{error}</div>}

      {!loading && (
        <form onSubmit={onSave} style={styles.form}>
          {/* Roster meta */}
          <div style={styles.row}>
            <label style={styles.label}>Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              style={styles.input}
              placeholder="e.g., 7th Grade Homeroom"
            />
          </div>

          <div style={styles.row}>
            <label style={styles.label}>Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={styles.input}
              placeholder="optional"
            />
          </div>

          {/* Filters — matches StudentSelectPage + robust data sources */}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              <strong>Students</strong>

              <input
                placeholder="Search by name"
                value={search}
                onChange={(e) => { setPage(1); setSearch(e.target.value); }}
                style={{ ...styles.input, maxWidth: 260, marginLeft: 8 }}
              />

              <select
                value={houseId}
                onChange={(e) => { setPage(1); setHouseId(e.target.value); }}
                style={styles.select}
              >
                <option value="">All Houses</option>
                {houses.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>

              <select
                value={grade}
                onChange={(e) => { setPage(1); setGrade(e.target.value); }}
                style={styles.select}
              >
                <option value="">All Grades</option>
                {allGrades.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>

              <button type="button" style={styles.secondaryBtn} onClick={onRefresh}>
                Refresh
              </button>
              <button type="button" style={styles.secondaryBtn} onClick={onReset}>
                Reset
              </button>
            </div>

            {/* Table */}
            <div style={styles.table}>
              <div style={styles.tableHead}>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={allOnPageChecked}
                    ref={headerRef}
                    onChange={togglePage}
                  />
                  Select Page
                </label>
                <div style={{ fontWeight: 600 }}>Name</div>
                <div style={{ fontWeight: 600 }}>House</div>
                <div style={{ fontWeight: 600 }}>Grade</div>
              </div>
              <div>
                {!err && rows.length === 0 && (
                  <div style={{ padding: 8, color: "#6B7280" }}>
                    No students found for the current filters.
                  </div>
                )}
                {err && (
                  <div style={{ padding: 8, color: "#b00020" }}>
                    {err}
                  </div>
                )}
                {rows.map((s) => {
                  const sid = toId(s);
                  const displayName = toDisplayName(s);
                  const houseDisplay = (() => {
                    const hn = s?.house_name;
                    const h = s?.house;
                    if (hn != null && String(hn).trim() !== "") return hn;
                    if (h != null && String(h).trim() !== "") return h;
                    return "—";
                  })();
                  const gradeDisplay = (() => {
                    const g = s?.grade ?? s?.grade_level;
                    return g != null && String(g).trim() !== "" ? g : "—";
                  })();

                  return (
                    <div key={sid} style={styles.tableRow}>
                      <div>
                        <input
                          type="checkbox"
                          checked={isSelected(sid)}
                          onChange={() => toggleOne(sid)}
                        />
                      </div>
                      <div>{displayName}</div>
                      <div>{houseDisplay}</div>
                      <div>{gradeDisplay}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination controls */}
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ color: "#4B5563", fontSize: 14 }}>
                Showing {rows.length}{typeof count === "number" ? ` of ${count}` : ""} students
                {selected.size > 0 && <span style={{ marginLeft: 8, fontWeight: 600 }}>• Selected: {selected.size}</span>}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <label style={{ fontSize: 14 }}>Page Size</label>
                <select
                  value={pageSize}
                  onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}
                  style={styles.select}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <button
                  type="button"
                  style={styles.secondaryBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  type="button"
                  style={styles.secondaryBtn}
                  disabled={totalPages ? page >= totalPages : rows.length < pageSize}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <button type="submit" style={styles.primaryBtn} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button type="button" style={styles.secondaryBtn} onClick={() => navigate("/rosters")}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 1000, margin: "0 auto", padding: 16 },
  form: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    padding: 12,
    background: "#fff",
  },
  row: { display: "flex", gap: 12, alignItems: "center", marginBottom: 8 },
  label: { width: 120, fontWeight: 600 },
  input: {
    flex: 1,
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 10px",
  },
  select: {
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "8px 10px",
  },
  table: {
    border: "1px solid #E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
    background: "#fff",
  },
  tableHead: {
    display: "grid",
    gridTemplateColumns: "120px 1fr 1fr 1fr",
    gap: 8,
    padding: 8,
    background: "#F3F4F6",
    borderBottom: "1px solid #E5E7EB",
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns: "120px 1fr 1fr 1fr",
    gap: 8,
    padding: 8,
    borderBottom: "1px solid #F3F4F6",
  },
  primaryBtn: {
    border: "1px solid #2563EB",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#2563EB",
    color: "#fff",
    cursor: "pointer",
  },
  secondaryBtn: {
    border: "1px solid #6B7280",
    borderRadius: 8,
    padding: "8px 12px",
    background: "#fff",
    color: "#374151",
    cursor: "pointer",
  },
  error: {
    color: "#b00020",
    background: "#FFEAEA",
    border: "1px solid #F5B5B5",
    borderRadius: 8,
    padding: "8px 10px",
    marginBottom: 8,
  },
};
